import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Alert, Anchor, Badge, Code, Group, Pagination, Skeleton as MSkeleton, Stack, Table, Text,
} from '@mantine/core'
import {
  IconAlertTriangle, IconDatabaseOff, IconLock, IconPlugConnectedX, IconUserOff,
} from '@tabler/icons-react'
import { EmptyState, SectionCard, TabStrip } from '@lighthouse/design'
import { RestrictedState } from '../access/RestrictedState'
import { useLighthouseAdmin } from '../../hooks/useLighthouseAdmin'
import { useRetirementEpisodes } from '../../hooks/useRetirementEpisodes'
import { isApiError, problemDetailText } from '../../services/apiError'
import {
  EPISODE_STATES, EPISODE_STATE_MEANING, NONE_CAN_YET, allZero, countFor, episodeStatusText,
} from '../../types/retirementEpisodes'
import type {
  RetirementEpisode, RetirementEpisodeCount, RetirementEpisodeState, RetirementEpisodes as EpisodesPayload,
  RetirementEpisodeSummary,
} from '../../types/retirementEpisodes'
import { NOT_MEASURED, errorKind, errorSummary, exactNumber, whenLabel } from '../../utils/format'
import { reportDetailPath } from '../../utils/reportDetailPath'

/** The server's largest page, and the candidate list's own. */
const PAGE_SIZE = 50

const ALL = 'all'

const SURFACE = 'Retirement episodes'

/**
 * Episodes — what the consent clock has done to each report it has looked at.
 * Read-only; mounted below the candidate list, and self-contained: it gates,
 * fetches and fails on its own, so its table's absence never blocks the list.
 *
 * FOUR STATES THAT MUST NOT LOOK ALIKE, and today the first is the common one:
 *
 *  1. **500 — the table does not exist.** Migration AddRetirementCandidate is
 *     applied nowhere yet, so the endpoint fails with "Invalid object name". That
 *     is "episode history unavailable", never "no episodes".
 *  2. **403** — the caller lacks Lighthouse.Admin: RestrictedState, neutral.
 *  3. **Empty table** — the migration is applied but the 04:00 sweep has not run,
 *     or found nothing eligible.
 *  4. **Rows.**
 *
 * NOTHING HERE IS GREEN. An Open episode is a report on the consent clock whose
 * clock cannot start: every one reads "awaiting a notice that cannot be sent yet"
 * while no notice has been recorded, because the notification channel (doc 10) is
 * not built. And the delist and reversal sections are empty everywhere for a
 * structural reason the server states — automatic delisting is locked in code —
 * so their empty state says "none has happened, and none can yet", not "all clear".
 *
 * An episode's report name opens Report Detail on its Governance tab (D5) — but
 * only while the report still exists: a purged report's id answers 404 there, so
 * its snapshot name stays plain text rather than a link to nothing.
 */
export function RetirementEpisodes() {
  const gate = useLighthouseAdmin()

  // The gate contract: on `denied`, never fetch an endpoint the caller cannot read.
  // On `pending` (a cold load, before the catalogue answers) wait rather than race
  // the answer. `unknown` renders the body, which handles its own 403.
  if (gate.state === 'denied') {
    return (
      <SectionCard title="Episodes">
        <RestrictedState surface={SURFACE} reason={gate.reason} />
      </SectionCard>
    )
  }
  if (gate.state === 'pending') {
    return (
      <SectionCard title="Episodes">
        <Stack gap="sm" aria-busy="true" data-state="gate-pending">
          <MSkeleton height={56} />
          <MSkeleton height={200} />
        </Stack>
      </SectionCard>
    )
  }

  return <EpisodesBody />
}

function EpisodesBody() {
  const [tab, setTab] = useState<string>(ALL)
  const [page, setPage] = useState(1)

  const state = tab === ALL ? null : (tab as RetirementEpisodeState)
  const { data, isLoading, isError, error } = useRetirementEpisodes({
    state,
    skip: (page - 1) * PAGE_SIZE,
    top: PAGE_SIZE,
  })

  return (
    <SectionCard
      title="Episodes"
      subtitle="What the consent clock has done to each report it has looked at — read-only; every action on a report is Cartographer's"
    >
      {isError ? (
        <EpisodesError error={error} />
      ) : isLoading || !data ? (
        // `|| !data` is load-bearing: between retries the flags read "not loading,
        // not errored" with no payload, and rendering then shows only defaults.
        <Stack gap="sm">
          <MSkeleton height={56} />
          <MSkeleton height={32} />
          <MSkeleton height={200} />
        </Stack>
      ) : (
        <EpisodesContent
          payload={data}
          tab={tab}
          page={page}
          onTab={(value) => {
            setTab(value)
            setPage(1)
          }}
          onPage={setPage}
        />
      )}
    </SectionCard>
  )
}

function EpisodesContent({
  payload, tab, page, onTab, onPage,
}: {
  payload: EpisodesPayload
  tab: string
  page: number
  onTab: (value: string) => void
  onPage: (page: number) => void
}) {
  const { summary, rows } = payload
  const totalPages = Math.max(1, Math.ceil(payload.totalCount / PAGE_SIZE))
  const locked = summary.automaticDelistLockedBecause

  const tabs = [
    { value: ALL, label: `All (${exactNumber(summary.totalEpisodes)})` },
    ...EPISODE_STATES.map((s) => {
      const n = countFor(summary.byState, s)
      return { value: s, label: `${s} (${n == null ? NOT_MEASURED : exactNumber(n)})` }
    }),
  ]

  return (
    <Stack gap="md">
      <LockNotice lockedBecause={locked} />

      <Group gap="xl" align="flex-start" wrap="wrap">
        <OutcomeCounts
          title="Delists"
          counts={summary.delistsByBasis}
          labels={{ OwnerSilence: "By the owner's silence", Decision: "By a person's decision" }}
          emptyText={locked != null ? NONE_CAN_YET : 'none has happened'}
        />
        <OutcomeCounts
          title="Reversals"
          counts={summary.reversalsByKind}
          labels={{ Person: 'By a person', EvidenceOfUse: 'On evidence of use', OutOfBand: 'Out of band' }}
          emptyText={reversalEmptyText(summary)}
        />
      </Group>

      <TabStrip tabs={tabs} value={tab} onChange={onTab} />
      {tab !== ALL && (
        <Text size="xs" c="dimmed">{EPISODE_STATE_MEANING[tab as RetirementEpisodeState]}</Text>
      )}

      {rows.length === 0 ? (
        <Text size="sm" c="dimmed">{emptyRowsText(summary, tab, locked)}</Text>
      ) : (
        <>
          <Table.ScrollContainer minWidth={960}>
            <Table striped>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Report</Table.Th>
                  <Table.Th>Where it stands</Table.Th>
                  <Table.Th>Signals</Table.Th>
                  <Table.Th>Addressed to</Table.Th>
                  <Table.Th>Idle since</Table.Th>
                  <Table.Th>Opened</Table.Th>
                  <Table.Th>Ended</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {rows.map((row) => <EpisodeRow key={row.id} row={row} />)}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
          {totalPages > 1 && (
            <Group justify="space-between">
              <Text size="sm" c="dimmed">
                Page {page} of {totalPages} · {exactNumber(payload.totalCount)} episodes
              </Text>
              <Pagination value={page} total={totalPages} onChange={onPage} />
            </Group>
          )}
        </>
      )}
    </Stack>
  )
}

/**
 * The lock, in the server's words. Never green: it is the reason nothing below
 * has happened, and the day it is null the page says so plainly rather than
 * celebrating.
 */
function LockNotice({ lockedBecause }: { lockedBecause: string | null }) {
  if (lockedBecause == null) {
    return (
      <Alert icon={<IconAlertTriangle size={16} />} color="yellow" variant="light" title="Automatic delisting is permitted">
        <Text size="sm">
          The server reports no lock on automatic delisting: an owner who was notified and stayed silent past the
          grace period can now have a report delisted by the nightly sweep.
        </Text>
      </Alert>
    )
  }

  return (
    <Alert icon={<IconLock size={16} />} color="gray" variant="light" title="Automatic delisting is locked">
      <Stack gap={4}>
        <Text size="sm">No report can be delisted automatically: {lockedBecause}.</Text>
        <Text size="sm">
          And no notice can be sent to start any grace period, because the notification channel (doc 10) is not
          built — so an open episode waits indefinitely, and that is expected.
        </Text>
      </Stack>
    </Alert>
  )
}

function OutcomeCounts({
  title, counts, labels, emptyText,
}: {
  title: string
  counts: RetirementEpisodeCount[]
  labels: Record<string, string>
  emptyText: string
}) {
  return (
    <Stack gap={4} miw={220}>
      <Text size="sm" fw={600}>{title}</Text>
      {allZero(counts) ? (
        <Text size="sm" c="dimmed">{capitalise(emptyText)}.</Text>
      ) : (
        counts.map((c) => (
          <Group key={c.name} gap="xs" wrap="nowrap">
            <Text size="sm" ff="monospace" miw={32} ta="right">{exactNumber(c.count)}</Text>
            <Text size="sm">{labels[c.name] ?? c.name}</Text>
          </Group>
        ))
      )}
    </Stack>
  )
}

function EpisodeRow({ row }: { row: RetirementEpisode }) {
  const navigate = useNavigate()
  const note = row.reversalNote ?? row.keepReason ?? row.resolutionNote
  const detail = row.reportStillExists ? reportDetailPath(row.savedReportId, 'governance') : null

  return (
    <Table.Tr>
      <Table.Td>
        <Stack gap={2} align="flex-start">
          {detail ? (
            <Anchor
              component="button"
              type="button"
              size="sm"
              fw={500}
              ta="left"
              data-report-detail={detail}
              onClick={() => navigate(detail)}
            >
              {row.reportNameSnapshot}
            </Anchor>
          ) : (
            <Text size="sm" fw={500}>{row.reportNameSnapshot}</Text>
          )}
          {!row.reportStillExists ? (
            <Text size="xs" c="dimmed">No longer exists — purged since this episode</Text>
          ) : row.currentReportName !== row.reportNameSnapshot || row.currentStatus !== 'Active' ? (
            <Text size="xs" c="dimmed">
              Now: {row.currentReportName ?? NOT_MEASURED} · {row.currentStatus ?? NOT_MEASURED}
            </Text>
          ) : null}
          <Text size="xs" c="dimmed">{row.reason}</Text>
        </Stack>
      </Table.Td>
      <Table.Td>
        <Stack gap={2}>
          {/* Neutral text for every state — an Open row is never painted as healthy. */}
          <Text size="sm">{episodeStatusText(row)}</Text>
          {note && <Text size="xs" c="dimmed">&ldquo;{note}&rdquo;</Text>}
        </Stack>
      </Table.Td>
      <Table.Td>
        {row.signals.length === 0 ? (
          <Text size="sm" c="dimmed">{NOT_MEASURED}</Text>
        ) : (
          <Group gap={4}>
            {row.signals.map((s) => <Badge key={s} variant="light" color="gray" size="sm">{s}</Badge>)}
          </Group>
        )}
      </Table.Td>
      <Table.Td>
        <Text size="sm">{addressedTo(row)}</Text>
      </Table.Td>
      <Table.Td>
        <Text size="sm">{whenLabel(row.idleSinceAt)}</Text>
      </Table.Td>
      <Table.Td>
        <Text size="sm">{whenLabel(row.createdAt)}</Text>
      </Table.Td>
      <Table.Td>
        {row.state === 'Open' ? (
          <Text size="sm" c="dimmed">Still open</Text>
        ) : (
          <Stack gap={2}>
            <Text size="sm">{whenLabel(row.resolvedAt)}</Text>
            {endedBy(row) && <Text size="xs" c="dimmed">{endedBy(row)}</Text>}
            {row.reversedAt != null && (
              <Text size="xs" c="dimmed">
                Reversed {whenLabel(row.reversedAt)}
                {row.reversedByDisplayName ? ` by ${row.reversedByDisplayName}` : ''}
              </Text>
            )}
          </Stack>
        )}
      </Table.Td>
    </Table.Tr>
  )
}

/**
 * Failure, by status — the four cases a reader must be able to tell apart. A 500
 * is the missing table far more often than anything else today, and saying so is
 * the difference between "broken" and "not yet applied".
 */
function EpisodesError({ error }: { error: unknown }) {
  const kind = errorKind(error)
  const summary = errorSummary(error)
  const detail = summary ? <Code style={{ overflowWrap: 'anywhere' }}>{summary}</Code> : undefined

  if (kind === 'forbidden') {
    return <RestrictedState surface={SURFACE} reason={isApiError(error) ? problemDetailText(error.body) : null} />
  }

  if (kind === 'server') {
    return (
      <EmptyState
        icon={<IconDatabaseOff size={28} stroke={1.5} />}
        title="Episode history unavailable — migration AddRetirementCandidate may not be applied"
        message={
          'The episodes table (ReportBuilder.RetirementCandidate) is created by migration AddRetirementCandidate, '
          + 'which is applied by hand and, as of this build, nowhere yet. Until it is, this request fails and nothing '
          + 'is known here about any episode — this is not the same as there being none. The candidate list above '
          + 'does not depend on that table and is unaffected.'
        }
        action={detail}
      />
    )
  }

  if (kind === 'unauthorized') {
    return (
      <EmptyState
        icon={<IconUserOff size={28} stroke={1.5} />}
        title="Not signed in"
        message="The API did not accept this session's credentials, so no episode could be read. Sign in again."
        action={detail}
      />
    )
  }

  if (kind === 'network') {
    return (
      <EmptyState
        icon={<IconPlugConnectedX size={28} stroke={1.5} />}
        title="The API could not be reached"
        message="The request never got an answer, so nothing is known here about any episode."
        action={detail}
      />
    )
  }

  return (
    <EmptyState
      icon={<IconAlertTriangle size={28} stroke={1.5} />}
      title="Could not load retirement episodes"
      message="The request failed, so nothing is known here about any episode."
      action={detail}
    />
  )
}

function addressedTo(row: RetirementEpisode): string {
  if (row.routedTo === 'CartographerAdmins') return 'Cartographer administrators'
  return row.routedToDisplayName ?? `The owner (name ${NOT_MEASURED})`
}

/** Who ended a terminal episode: a person for a keep or a decision, otherwise the nightly sweep. */
function endedBy(row: RetirementEpisode): string | null {
  const byPerson = row.state === 'Kept' || (row.state === 'Delisted' && row.delistBasis === 'Decision')
  if (byPerson) return `by ${row.resolvedByDisplayName ?? NOT_MEASURED}`
  return row.resolvedAt != null ? 'by the nightly sweep' : null
}

function reversalEmptyText(summary: RetirementEpisodeSummary): string {
  // A reversal needs a delist first; while none exists and the lock holds, none can.
  const noDelists = allZero(summary.delistsByBasis)
  return noDelists && summary.automaticDelistLockedBecause != null ? NONE_CAN_YET : 'none has happened'
}

function emptyRowsText(summary: RetirementEpisodeSummary, tab: string, locked: string | null): string {
  if (summary.totalEpisodes === 0) {
    return 'No episodes yet. The table fills only after the 04:00 report-retirement-sweep has run, and the sweep '
      + 'opens one only for an Active, idle, user-owned report with no schedule, favourite, open request or keep.'
  }
  if (tab === 'Delisted' && locked != null) return `No delisted episodes: ${NONE_CAN_YET}.`
  return tab === ALL ? 'No episodes on this page.' : `No ${tab} episodes.`
}

function capitalise(text: string): string {
  return text.length === 0 ? text : text[0].toUpperCase() + text.slice(1)
}
